import { useState } from 'react'
import clsx from 'clsx'
import styles from './controls.module.css'
import type { FilterDistGroup, FilterOption } from 'lib/dealhunt/types'

interface DistControlProps {
  group: FilterDistGroup
  onOption: (option: FilterOption, on: boolean) => void
}

/**
 * High-cardinality values: a segmented share bar, the top-N rows with their share,
 * "+N more", and the backend's sentence ("IPX5 appears on 13% of the 264 …").
 * The `other` option aggregates the tail; its param expands server-side.
 */
const DistControl: React.VFC<DistControlProps> = ({ group, onOption }) => {
  const [expanded, setExpanded] = useState(false)
  const rows = expanded ? group.options : group.options.slice(0, group.top)
  const hidden = group.options.length - rows.length
  const all = group.other ? [...group.options, group.other] : group.options
  const total = all.reduce((sum, o) => sum + o.count, 0) || 1
  const pct = (o: FilterOption): number => Math.round(((o.share ?? o.count / total) || 0) * 100)

  return (
    <>
      <div className={styles.distBar} aria-hidden="true">
        {all.map(opt => (
          <button
            key={opt.value}
            type="button"
            tabIndex={-1}
            className={clsx(styles.distSeg, {
              [styles.distSegActive]: opt.selected,
              [styles.distSegOther]: opt === group.other,
            })}
            style={{ flexGrow: Math.max(opt.count, 1) }}
            title={`${opt.label} · ${pct(opt)}%`}
            onClick={() => onOption(opt, !opt.selected)}
          />
        ))}
      </div>
      <div className={styles.distRows}>
        {rows.map(opt => (
          <button
            key={opt.value}
            type="button"
            className={clsx(styles.distRow, { [styles.distRowActive]: opt.selected })}
            aria-pressed={opt.selected}
            onClick={() => onOption(opt, !opt.selected)}
          >
            <span
              className={clsx(styles.distDot, { [styles.distDotActive]: opt.selected })}
              aria-hidden="true"
            />
            {opt.label}
            <span className={styles.distShare}>{`${pct(opt)}%`}</span>
          </button>
        ))}
        {group.other && (
          <button
            type="button"
            className={clsx(styles.distRow, { [styles.distRowActive]: group.other.selected })}
            aria-pressed={group.other.selected}
            onClick={() => group.other && onOption(group.other, !group.other.selected)}
          >
            <span className={clsx(styles.distDot, styles.distSegOther)} aria-hidden="true" />
            {group.other.label}
            <span className={styles.distShare}>{`${pct(group.other)}%`}</span>
          </button>
        )}
      </div>
      {hidden > 0 && (
        <button type="button" className={styles.showMore} onClick={() => setExpanded(true)}>
          {`+${hidden} more`}
        </button>
      )}
      {expanded && group.options.length > group.top && (
        <button type="button" className={styles.showMore} onClick={() => setExpanded(false)}>
          {'Show fewer'}
        </button>
      )}
      {group.copy.sentence && <p className={styles.sentence}>{group.copy.sentence}</p>}
    </>
  )
}

export default DistControl
